import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { DOCUMENT_KINDS, type Output } from '@jabberr/core';
import { Markdown } from '../components/Markdown.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { WorkbenchSave, isNarrow } from '../components/WorkbenchSave.js';
import { api, errorMessage } from '../lib/api.js';
import { conversationTitle, relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';

function kindLabel(output: Output): string {
  return output.purpose === 'saved' ? 'Kept reply' : DOCUMENT_KINDS[output.purpose].label;
}

function fileName(output: Output): string {
  const base = (output.conversationTitle ?? 'jabberr').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'jabberr';
  const kind = kindLabel(output).toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `${base}-${kind}-${output.createdAt.slice(0, 10)}.md`;
}

export function OutputPage() {
  const { projectId = '', turnId = '' } = useParams();
  const [output, setOutput] = useState<Output | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [linked, setLinked] = useState<boolean | null>(null);
  // setInfo alone (see OutputsPage). Without it the phone top bar kept the last conversation's title and its More menu.
  const { setInfo } = useDrawer();
  useEffect(() => {
    setInfo({ projectId, title: output ? kindLabel(output) : 'Saved document' });
  }, [projectId, output, setInfo]);

  useEffect(() => {
    // Whether the project is linked to a Workbench project, so an unlinked one says so before anyone presses Save.
    api.projects
      .get(projectId)
      .then((r) => setLinked(Boolean(r.project.workbenchProjectId)))
      .catch(() => setLinked(null));
  }, [projectId]);

  useEffect(() => {
    api.outputs
      .get(projectId, turnId)
      .then((r) => {
        setOutput(r.output);
        setMarkdown(r.markdown);
        setCanSave(r.canSaveToWorkbench === true);
      })
      .catch((err) => setError(errorMessage(err)));
  }, [projectId, turnId]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  // A phone (and the iPhone app's web view, which cannot save a blob) gets Copy as the main action and no Download.
  const narrow = isNarrow();
  // Save to the Workbench stays the main action while it is on offer; otherwise, on a phone, Copy is.
  const saveOffered = canSave && !output?.workbench;

  const download = (): void => {
    if (!output) return;
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName(output);
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{output ? kindLabel(output) : 'Output'}</h1>
          {output ? (
            <span className="muted small">
              from <Link to={`/p/${projectId}/c/${output.conversationId}`}>{conversationTitle(output.conversationTitle)}</Link> · {relativeTime(output.createdAt)}
            </span>
          ) : null}
        </div>
        <div className="row output-actions">
          <Link to={`/p/${projectId}/outputs`} className="btn">
            All outputs
          </Link>
          <button type="button" className={`btn ${narrow && !saveOffered ? 'btn-primary' : ''}`} onClick={() => void copy()} disabled={!markdown}>
            {copied ? 'Copied' : narrow ? 'Copy' : 'Copy markdown'}
          </button>
          {narrow ? null : (
            <button type="button" className="btn" onClick={download} disabled={!markdown}>
              Download .md
            </button>
          )}
          {output ? (
            <WorkbenchSave
              projectId={projectId}
              turnId={turnId}
              workbench={output.workbench}
              canSave={canSave}
              linked={linked}
              disabled={!markdown}
              onSaved={(workbench) => setOutput({ ...output, workbench })}
            />
          ) : null}
        </div>
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {!output && !error ? (
        <Spinner label="Loading" />
      ) : (
        <div className="card preview-area output-view">
          <Markdown text={markdown} />
        </div>
      )}
    </div>
  );
}
